/**
 * Stage badge styling.
 *
 * `template.stageStyles` is a sparse override map keyed by stage — the server
 * owns the defaults and regenerates the real CSS on every render, so the editor
 * only ever sends what the org actually changed. The same rules are generated
 * here so the canvas and the live preview look like the PDF before saving.
 *
 * Property names and the `.stage-{key}` class mirror the server contract in
 * documentTemplates/utils/stageCss.js.
 */

/** Only properties the server accepts — anything else is rejected on save. */
export const STAGE_STYLE_PROPERTIES = [
  "color",
  "backgroundColor",
  "fontWeight",
  "fontSize",
  "textTransform",
];

const CSS_PROPERTY = {
  color: "color",
  backgroundColor: "background-color",
  fontWeight: "font-weight",
  fontSize: "font-size",
  textTransform: "text-transform",
};

export const STAGE_FONT_WEIGHT_OPTIONS = [
  { value: "400", label: "Regular" },
  { value: "600", label: "Semibold" },
  { value: "700", label: "Bold" },
];

export const DEFAULT_STAGE_COLOR = "#475467";
export const DEFAULT_STAGE_FONT_WEIGHT = "700";

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `abc` / `#ABC` / `#aabbcc` → `#aabbcc`; anything else → "". */
export const normalizeHexColor = (value) => {
  const clean = String(value ?? "").trim();
  if (!clean) return "";

  const withHash = clean.startsWith("#") ? clean : `#${clean}`;
  if (!HEX_COLOR.test(withHash)) return "";

  const hex = withHash.slice(1).toLowerCase();
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : hex;

  return `#${full}`;
};

export const stageClassName = (stage) =>
  stage?.className || `stage-${stage?.key || ""}`;

const cleanValue = (property, value) => {
  if (value == null || value === "") return "";
  if (property === "color" || property === "backgroundColor") {
    return normalizeHexColor(value);
  }
  return String(value).trim();
};

/** Catalog defaults for one stage, with unset properties as "". */
export const stageDefaultStyle = (stage) => {
  const defaults = stage?.defaults || {};
  const resolved = STAGE_STYLE_PROPERTIES.reduce((acc, property) => {
    acc[property] = cleanValue(property, defaults[property]);
    return acc;
  }, {});

  return {
    ...resolved,
    color: resolved.color || DEFAULT_STAGE_COLOR,
    fontWeight: resolved.fontWeight || DEFAULT_STAGE_FONT_WEIGHT,
  };
};

/** Catalog defaults with the org overrides applied on top. */
export const mergeStageStyles = (catalog = [], overrides = {}) =>
  catalog.reduce((acc, stage) => {
    const defaults = stageDefaultStyle(stage);
    const custom = overrides?.[stage.key] || {};

    acc[stage.key] = STAGE_STYLE_PROPERTIES.reduce((style, property) => {
      style[property] = cleanValue(property, custom[property]) || defaults[property];
      return style;
    }, {});

    return acc;
  }, {});

/**
 * Only what differs from the catalog defaults. Empty values are treated as
 * "not overridden" — the server drops them anyway and falls back to defaults.
 */
export const toStageStyleOverrides = (catalog = [], styles = {}) =>
  catalog.reduce((acc, stage) => {
    const current = styles?.[stage.key];
    if (!current) return acc;

    const defaults = stageDefaultStyle(stage);
    const diff = STAGE_STYLE_PROPERTIES.reduce((changed, property) => {
      const value = cleanValue(property, current[property]);
      if (value && value !== defaults[property]) changed[property] = value;
      return changed;
    }, {});

    if (Object.keys(diff).length) acc[stage.key] = diff;
    return acc;
  }, {});

/** Drop stages/properties the server would reject, e.g. after a catalog change. */
export const sanitizeStageStyleOverrides = (catalog = [], overrides = {}) => {
  const known = new Set(catalog.map((stage) => stage.key));

  return Object.entries(overrides || {}).reduce((acc, [key, style]) => {
    if (!known.has(key) || !style || typeof style !== "object") return acc;

    const clean = STAGE_STYLE_PROPERTIES.reduce((result, property) => {
      const value = cleanValue(property, style[property]);
      if (value) result[property] = value;
      return result;
    }, {});

    if (Object.keys(clean).length) acc[key] = clean;
    return acc;
  }, {});
};

export const stageStyleOverridesEqual = (a = {}, b = {}) => {
  const stable = (value) =>
    JSON.stringify(
      Object.keys(value || {})
        .sort()
        .map((key) => [
          key,
          Object.keys(value[key] || {})
            .sort()
            .map((property) => [property, value[key][property]]),
        ])
    );

  return stable(a) === stable(b);
};

const declarationsFor = (
  style,
  properties = STAGE_STYLE_PROPERTIES,
  important = false
) =>
  properties
    .filter((property) => style?.[property])
    .map((property) => {
      const suffix = important ? " !important" : "";
      return `${CSS_PROPERTY[property]}: ${style[property]}${suffix};`;
    })
    .join(" ");

/**
 * One rule per catalog stage. The compound `.stage.stage-paid` selector is
 * emitted alongside the plain one so these rules win over the template's own
 * `.stage-paid` regardless of stylesheet order in the canvas.
 */
export const buildStageCss = (catalog = [], overrides = {}) => {
  if (!catalog.length) return "";
  const merged = mergeStageStyles(catalog, overrides);

  return catalog
    .map((stage) => {
      const declarations = declarationsFor(merged[stage.key]);
      if (!declarations) return "";

      const className = stageClassName(stage);
      return `.${className},\n.stage.${className} { ${declarations} }`;
    })
    .filter(Boolean)
    .join("\n");
};

/**
 * Canvas variant of {@link buildStageCss}.
 *
 * Two things make the editor different from the rendered document:
 *
 * 1. The badge still carries the unresolved expression
 *    (`class="stage stage-{{invoice.stageClass}}"`), so no `.stage-{key}` rule
 *    matches it. The canvas previews one stage at a time, so the base `.stage`
 *    class is styled as that stage instead — which works whether the modifier
 *    class is resolved, unresolved or missing. The class is doubled to outrank
 *    a template's own `.stage-{key}` rule without resorting to `!important`.
 *
 * 2. The badge text is a token `<span class="hb-example">`. A rule matching
 *    that span directly beats any colour inherited from the badge, so the
 *    token is targeted explicitly rather than left to inherit.
 */
export const buildCanvasStageCss = (
  catalog = [],
  overrides = {},
  activeStageKey = ""
) => {
  const base = buildStageCss(catalog, overrides);
  const active = mergeStageStyles(catalog, overrides)[activeStageKey];
  if (!active) return base;

  const badge = declarationsFor(active, STAGE_STYLE_PROPERTIES, true);
  if (!badge) return base;

  // Background stays on the badge so the token does not paint a second chip
  const token = declarationsFor(
    active,
    ["color", "fontWeight", "fontSize", "textTransform"],
    true
  );

  return [
    base,
    /* Doubled .stage + !important: beats token resets and unresolved
       `stage-{{…}}` class names on the canvas. */
    `.stage.stage { ${badge} }`,
    `.stage.stage .hb-example,\n.stage.stage .hb-token,\n.stage.stage [data-hb-key],\n.stage.stage [data-hb-type="text"] { ${token} }`,
  ].join("\n");
};

/** Inline style for the badge preview chip shown next to each stage row. */
export const stageChipStyle = (style = {}) => ({
  color: style.color || DEFAULT_STAGE_COLOR,
  fontWeight: style.fontWeight || DEFAULT_STAGE_FONT_WEIGHT,
  backgroundColor: style.backgroundColor || "transparent",
  textTransform: style.textTransform || "uppercase",
  ...(style.fontSize ? { fontSize: style.fontSize } : {}),
});

/** Normalise whatever the stage-catalog endpoint returns into catalog entries. */
export const normalizeStageCatalog = (payload, type) => {
  const raw =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.stages) && payload.stages) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(payload?.data?.stages) && payload.data.stages) ||
    (payload && typeof payload === "object"
      ? Object.entries(payload.stages || payload.data || payload).map(
          ([key, value]) =>
            value && typeof value === "object" ? { key, ...value } : null
        )
      : []);

  return (raw || [])
    .filter((stage) => stage && (stage.key || stage.value))
    .map((stage) => {
      const key = String(stage.key || stage.value).trim().toLowerCase();
      return {
        key,
        label: stage.label || stage.name || key,
        entity: stage.entity || type,
        className: stage.className || `stage-${key}`,
        defaults: stage.defaults || stage.style || {},
      };
    });
};
